import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Calendar, FileText, Target, Users, TrendingUp, AlertCircle, CheckCircle, Clock, ExternalLink, Star, Lightbulb, BarChart3, Sparkles, ArrowRight, BookOpen, Award, Brain, Zap, RefreshCw, Plus, MapPin, GraduationCap, Heart, DollarSign, Building } from 'lucide-react';

const CollegeStrategyPage = () => {
  const [activeTab, setActiveTab] = useState('strategy');
  const [expandedSections, setExpandedSections] = useState({
    detailedStats: false,
    valueProposition: false,
    strategicResearch: false,
    completeBreakdown: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      
      {/* Hero Header - Black */}
      <div className="bg-slate-900 text-white border-b-4 border-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">University of Chicago</h1>
                <div className="flex items-center gap-3 text-stone-300">
                  <span>Chicago, IL</span>
                  <span>•</span>
                  <span>7,470 students</span>
                  <span>•</span>
                  <span className="px-2 py-0.5 bg-indigo-500 text-white text-sm font-semibold rounded">REACH</span>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="bg-slate-800 rounded-lg px-4 py-3 border border-slate-700">
                <div className="text-stone-400 text-xs mb-1 uppercase tracking-wide">Application</div>
                <select className="bg-slate-700 text-white px-3 py-1 rounded border border-slate-600 text-sm font-medium">
                  <option>Regular Decision</option>
                  <option>Early Decision</option>
                  <option>Early Action</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-stone-100 border-b-2 border-slate-900">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex gap-1">
            {[
              { id: 'strategy', label: 'Strategy & Fit', badge: null },
              { id: 'actions', label: 'Action Items', badge: 3 },
              { id: 'application', label: 'Application Requirements', badge: null },
              { id: 'research', label: 'School Research', badge: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-4 font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-slate-900 border-t-4 border-t-teal-600'
                    : 'text-stone-600 hover:text-slate-900 hover:bg-stone-200'
                }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-coral-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        
        {/* STRATEGY & FIT TAB */}
        {activeTab === 'strategy' && (
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2 space-y-6">
              
              {/* Intelligent Insights */}
              <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
                <div className="bg-white px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Brain className="w-5 h-5 text-slate-900" />
                    <h2 className="text-md font-bold text-slate-900">INTELLIGENT INSIGHTS</h2>
                  </div>
                  <button className="p-2 hover:bg-stone-50 rounded transition-all">
                    <RefreshCw className="w-4 h-4 text-stone-600" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-slate-900" />
                      <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">VALUES MATCH</span>
                    </div>
                    <p className="text-slate-900 leading-relaxed text-sm">
                      UChicago's Core Curriculum requirement that all students engage with diverse intellectual traditions - from ancient philosophy to modern economics - directly supports your interdisciplinary interests and demonstrates the university's commitment to rigorous intellectual inquiry over pre-professional tracking
                    </p>
                  </div>

                  <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-slate-900" />
                      <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">ACADEMIC FIT</span>
                    </div>
                    <p className="text-slate-900 leading-relaxed text-sm">
                      With UChicago's 4.5% acceptance rate, position yourself around intellectual curiosity rather than achievements - their admissions essays famously reward unconventional thinking, so use prompts like 'What's so odd about odd numbers?' to showcase analytical creativity over traditional metrics
                    </p>
                  </div>

                  <div className="bg-stone-50 border border-stone-300 rounded-r-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-slate-900" />
                      <span className="text-sm font-bold text-teal-900 uppercase tracking-wide">LOCATION</span>
                    </div>
                    <p className="text-slate-900 leading-relaxed text-sm">
                      Chicago's Loop district houses major consulting firms like McKinsey & Company and BCG, plus the Chicago Mercantile Exchange - leverage UChicago's Metcalf Internship Program to secure funded positions at these firms, particularly strong given the university's economics department reputation
                    </p>
                  </div>
                </div>
              </div>

              {/* Strategic Intelligence */}
              <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
                <div className="bg-stone-50  px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-slate-900" />
                    <div>
                      <h2 className="text-md font-bold text-slate-900">YOUR STRATEGIC EDGE</h2>
                      <p className="text-sm text-stone-600">Intelligent Insights</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                 

                  <div className="pt-6 border-t-2 border-stone-200">
                    

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2">1. Perfect Alignment with UChicago's "Life of the Mind" Philosophy</h4>
                        <p className="text-stone-700 text-sm leading-relaxed text-sm">
                          You embody exactly what UChicago seeks—a student who chooses intellectual rigor over grade optimization. Your willingness to sacrifice class rank for deeper engagement with complex material demonstrates the authentic intellectual curiosity that thrives in UChicago's famously rigorous Core Curriculum.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-bold text-slate-900 mb-2">2. Natural Fit for UChicago's Contrarian Academic Culture</h4>
                        <p className="text-stone-700 text-sm leading-relaxed text-sm">
                          Your identity as a "principled contrarian" aligns perfectly with UChicago's tradition of challenging conventional wisdom. The university that gave us the Chicago School of Economics encourages students to "think otherwise" - you're not just academically strong, you're intellectually fearless.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              <div className="flex flex-row gap-4">
              <h3 className="font-bold text-slate-900">Timeline Health</h3>
              <p className="text-sm text-stone-600">
              Health Score: 85/100
              </p>
              <p className="text-sm text-stone-600">
              Pressure: High
              </p>
              <p className="text-sm text-stone-600">
                0/6 Tasks Completed
              </p>
              <p className="text-sm text-stone-600">
                2 Tasks Overdue
              </p>
              <p className="text-sm text-stone-600">
                2 Tasks Due Soon
              </p>

              </div>
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                  <h3 className="font-bold text-slate-900">Quick Context</h3>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Your fit strength</span>
                    <span className="font-bold text-teal-700">Strong Match</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Admission likelihood</span>
                    <span className="font-bold text-gold-700">Competitive</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Early Decision</span>
                    <span className="font-bold text-slate-900">Consider</span>
                  </div>
                </div>
              </div>
              <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-4 h-4 text-slate-900" />
                      <h3 className="font-bold text-slate-900">What This School Values</h3>
                    </div>
                    
                    <div className="mb-4">
                      <div className="font-bold text-slate-900 mb-1">Academic Excellence</div>
                      <p className="text-stone-700 text-sm">: Rigorous intellectual inquiry, Academic freedom, Interdisciplinary learning</p>
                    </div>

                    <div className="mb-4">
                      <div className="font-bold text-slate-900 mb-1">Additional Priorities</div>
                      <p className="text-stone-700 text-sm">: Critical thinking</p>
                    </div>

                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-300">
                      <p className="text-sm text-indigo-900">
                        <Lightbulb className="w-3 h-3 inline mr-1" />
                        Align your essays with these values by demonstrating how you embody them through specific examples and experiences.
                      </p>
                    </div>
                  </div>

              <button className="w-full p-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-all border border-slate-900 flex items-center justify-center gap-2">
                <Users className="w-4 h-4" />
                Compare with Other Schools
              </button>
            </div>
          </div>
        )}

        {/* ACTION ITEMS TAB */}
        {activeTab === 'actions' && (
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2 space-y-6">
              
              {/* Essays Section */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-white border-b-2 border-slate-900 px-6 py-4">
                  <h2 className="text-md font-bold text-slate-900">ESSAYS</h2>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                    <span className="font-bold text-teal-700">Essay slots created (1 slot)</span>
                  </div>
                  
                  <p className="text-sm text-stone-700 mb-4">
                    Visit the Essays module to start writing your essays for this college.
                  </p>

                  <div className="mb-4">
                    <h3 className="font-bold text-slate-900 mb-3">Essay Tasks</h3>
                    <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg border border-stone-300">
                      <div className="w-5 h-5 border border-stone-400 rounded mt-0.5"></div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 mb-1">
                          Generate Ideas for Supplemental Essays to University of Chicago
                        </div>
                        <div className="flex items-center gap-2 text-sm text-coral-600">
                          <Calendar className="w-3 h-3" />
                          <span>Oct 13</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Improve Your Odds - Expandable Actions */}
              <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-lg border border-violet-300 overflow-hidden">
                <button
                  onClick={() => toggleSection('immediateActions')}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-violet-100/50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Lightbulb className="w-5 h-5 text-violet-600" />
                    <div className="text-left">
                      <h2 className="text-md font-bold text-slate-900">Improve Your Odds</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="px-2 py-0.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold rounded-full">
                          STRATEGIC ADVANTAGE
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedSections.immediateActions ? (
                    <ChevronDown className="w-5 h-5 text-slate-900" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-900" />
                  )}
                </button>

                {expandedSections.immediateActions && (
                  <div className="border-t-2 border-violet-300 p-6 bg-white">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-4 h-4 text-teal-600" />
                      <h3 className="font-bold text-slate-900">Immediate Priorities (Next 4-6 weeks)</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Action Item 1 */}
                      <div className="bg-teal-50 border-l-4 border-teal-600 rounded-r-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-bold text-slate-900 mb-2">
                              Schedule and take the SAT/ACT by March 2024, targeting 1520+ SAT or 34+ ACT through 2-3 hours daily prep focusing on time management and question pattern recognition
                            </h4>
                            <p className="text-sm text-stone-700 mb-2">
                              UChicago's 4.5% acceptance rate requires test scores in top 5% nationally - your analytical skills suggest strong potential, but execution matters for reach schools
                            </p>
                            <p className="text-xs text-teal-700 italic mb-3">
                              ✓ You'll know this is working when you're consistently scoring within 50 points of your target on practice tests and can complete each section with 5+ minutes remaining
                            </p>
                            <button className="px-3 py-1.5 bg-teal-100 text-teal-800 text-sm font-medium rounded hover:bg-teal-200 transition-all">
                              + Add to My Tasks
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Action Item 2 */}
                      <div className="bg-teal-50 border-l-4 border-teal-600 rounded-r-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-bold text-slate-900 mb-2">
                              Identify and approach 2-3 teachers who've observed your analytical depth in challenging discussions for recommendation letters, specifically requesting meetings to discuss your intellectual growth
                            </h4>
                            <p className="text-sm text-stone-700 mb-2">
                              Strong recommendations are critical differentiators at elite schools - you need advocates who can speak to your 'graduate-level synthesis' abilities
                            </p>
                            <p className="text-xs text-teal-700 italic mb-3">
                              ✓ You'll know this is working when teachers reference specific examples of your analytical contributions and agree to highlight your intellectual integrity over grade-focused peers
                            </p>
                            <button className="px-3 py-1.5 bg-teal-100 text-teal-800 text-sm font-medium rounded hover:bg-teal-200 transition-all">
                              + Add to My Tasks
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Primary Development Focus */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <button
                  onClick={() => toggleSection('primaryDevelopment')}
                  className="w-full bg-azure-50 px-6 py-4 flex items-center justify-between hover:bg-azure-100 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-azure-600" />
                    <h2 className="text-md font-bold text-slate-900">Primary Development Focus</h2>
                  </div>
                  {expandedSections.primaryDevelopment ? (
                    <ChevronDown className="w-5 h-5 text-slate-900" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-900" />
                  )}
                </button>

                {expandedSections.primaryDevelopment && (
                  <div className="border-t-2 border-slate-900 p-6 space-y-4">
                    <div className="bg-azure-50 border-l-4 border-azure-600 rounded-r-lg p-4">
                      <h4 className="font-bold text-slate-900 mb-2">
                        Apply to and attend a competitive summer program in constitutional law, policy analysis, or political theory (Georgetown NSLC, Yale Young Global Scholars, or similar) where you can engage with graduate-level material
                      </h4>
                      <p className="text-sm text-stone-700 mb-2">
                        This connects to your pattern of 'choosing intellectual challenge over grade protection' and demonstrates readiness for university-level discourse
                      </p>
                      <p className="text-xs text-azure-700 mb-3">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        By February 2024, you should have submitted 2-3 competitive summer program applications with essays showcasing your analytical depth
                      </p>
                      <button className="px-3 py-1.5 bg-azure-100 text-azure-800 text-sm font-medium rounded hover:bg-azure-200 transition-all">
                        + Add to My Tasks
                      </button>
                    </div>

                    <div className="bg-azure-50 border-l-4 border-azure-600 rounded-r-lg p-4">
                      <h4 className="font-bold text-slate-900 mb-2">
                        Initiate an independent research project analyzing a current policy debate through multiple ethical frameworks, producing a 15-20 page paper suitable for submission to undergraduate research competitions
                      </h4>
                      <p className="text-sm text-stone-700 mb-2">
                        This showcases your natural ability to 'identify core tensions and moral complexities' in real-world applications, moving beyond classroom discussions
                      </p>
                      <p className="text-xs text-azure-700 mb-3">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        By May 2024, you should have completed research and submitted to at least 2 undergraduate competitions or conferences
                      </p>
                      <button className="px-3 py-1.5 bg-azure-100 text-azure-800 text-sm font-medium rounded hover:bg-azure-200 transition-all">
                        + Add to My Tasks
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* University-Specific Positioning */}
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <button
                  onClick={() => toggleSection('positioning')}
                  className="w-full bg-violet-50 px-6 py-4 flex items-center justify-between hover:bg-violet-100 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-violet-600" />
                    <h2 className="text-md font-bold text-slate-900">University of Chicago - Specific Positioning</h2>
                  </div>
                  {expandedSections.positioning ? (
                    <ChevronDown className="w-5 h-5 text-slate-900" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-900" />
                  )}
                </button>

                {expandedSections.positioning && (
                  <div className="border-t-2 border-slate-900 p-6 space-y-4">
                    <div className="bg-violet-50 border-l-4 border-violet-600 rounded-r-lg p-4">
                      <h4 className="font-bold text-slate-900 mb-2">
                        Attend UChicago's summer academic programs or virtual information sessions, then write a detailed follow-up email to admissions referencing specific faculty research or course offerings that align with your policy interests
                      </h4>
                      <p className="text-sm text-stone-700 mb-2">
                        UChicago values students who understand their unique intellectual culture - their emphasis on 'rigorous intellectual inquiry' and 'critical thinking' matches your analytical approach
                      </p>
                      <p className="text-xs text-violet-700 mb-3">
                        <Lightbulb className="w-3 h-3 inline mr-1" />
                        Research 2-3 specific professors whose work intersects with your interests, mention their research in your follow-up, and explain how their approach to policy analysis connects to your own intellectual development
                      </p>
                      <button className="px-3 py-1.5 bg-violet-100 text-violet-800 text-sm font-medium rounded hover:bg-violet-200 transition-all">
                        📝 Add to Essay Notes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                <div className="bg-coral-50 border-b-2 border-slate-900 px-4 py-3">
                  <h3 className="font-bold text-slate-900 text-sm">Critical Deadlines</h3>
                </div>
                <div className="p-4 space-y-2">
                  <div className="p-3 bg-coral-50 rounded-lg border border-coral-400">
                    <div className="text-coral-700 font-bold text-sm mb-1">Oct 15 (12 days)</div>
                    <div className="text-slate-900 font-medium text-sm">Essay draft</div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg border border-stone-300">
                    <div className="text-stone-700 font-bold text-sm mb-1">Oct 20 (17 days)</div>
                    <div className="text-slate-900 font-medium text-sm">Request recs</div>
                  </div>
                  <div className="p-3 bg-slate-900 text-white rounded-lg border border-slate-900">
                    <div className="text-teal-300 font-bold text-sm mb-1">Nov 1 (29 days)</div>
                    <div className="font-bold">ED DEADLINE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

         {/* APPLICATION REQUIREMENTS TAB */}
         {activeTab === 'application' && (
          <div className="space-y-8">
            
            {/* Application Overview Header */}
            <div className="bg-slate-900 text-white rounded-lg border border-slate-900 overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-slate-700">
                <h2 className="text-md font-bold">Application Overview</h2>
                <p className="text-sm text-stone-300 mt-1">Key deadlines and application components for University of Chicago</p>
              </div>
              <div className="grid grid-cols-5 gap-4 p-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-coral-400 mb-1">Jan 2</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">RD Deadline</div>
                  <div className="text-xs text-coral-300 mt-1">92 days left</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-teal-400 mb-1">Nov 1</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">ED Deadline</div>
                  <div className="text-xs text-teal-300 mt-1">29 days left</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">3</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Essays Required</div>
                  <div className="text-xs text-gold-300 mt-1">1 in progress</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">2</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Teacher Recs</div>
                  <div className="text-xs text-gold-300 mt-1">1 requested</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gold-400 mb-1">37%</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Complete</div>
                  <div className="text-xs text-stone-300 mt-1">3/8 components</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-8">
              {/* Left Column - Main Requirements */}
              <div className="col-span-2 space-y-6">
                
                {/* Application Components Checklist */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-gold-50 border-b-2 border-slate-900 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-gold-600" />
                        <h2 className="text-md font-bold text-slate-900">Application Components</h2>
                      </div>
                      <span className="text-md font-bold text-slate-900">3/8 Complete</span>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-3">
                    {[
                      { 
                        name: 'Common Application', 
                        status: 'complete', 
                        urgent: false,
                        details: 'Personal info, activities, honors submitted'
                      },
                      { 
                        name: 'Application Fee ($75) or Fee Waiver', 
                        status: 'complete', 
                        urgent: false,
                        details: 'Fee waiver approved'
                      },
                      { 
                        name: 'Official High School Transcript', 
                        status: 'complete', 
                        urgent: false,
                        details: 'Requested from counselor, sent Oct 1'
                      },
                      { 
                        name: 'Common App Essay (650 words)', 
                        status: 'in-progress', 
                        urgent: true,
                        details: 'Draft completed, needs revision',
                        deadline: 'Nov 1 for ED'
                      },
                      { 
                        name: 'UChicago Supplemental Essays (3 required)', 
                        status: 'not-started', 
                        urgent: true,
                        details: 'Why UChicago + 2 extended essays',
                        deadline: 'Nov 1 for ED'
                      },
                      { 
                        name: 'Teacher Recommendation #1 (Ms. Chen)', 
                        status: 'in-progress', 
                        urgent: true,
                        details: 'Requested Oct 1, pending submission',
                        deadline: 'Oct 20'
                      },
                      { 
                        name: 'Teacher Recommendation #2 (Mr. Patel)', 
                        status: 'not-started', 
                        urgent: true,
                        details: 'Need to request',
                        deadline: 'Oct 20'
                      },
                      { 
                        name: 'Counselor Recommendation', 
                        status: 'not-started', 
                        urgent: false,
                        details: 'Will be submitted with transcript',
                        deadline: 'Nov 1'
                      },
                      { 
                        name: 'SAT/ACT Scores (Optional - Test Optional)', 
                        status: 'not-started', 
                        urgent: false,
                        details: 'Your 1510 SAT - decide if submitting',
                        deadline: 'Nov 1'
                      },
                      { 
                        name: 'Mid-Year Report', 
                        status: 'not-started', 
                        urgent: false,
                        details: 'Submitted after Fall semester grades',
                        deadline: 'Feb 15'
                      }
                    ].map((item, idx) => (
                      <div 
                        key={idx}
                        className={`rounded-lg border p-4 transition-all ${
                          item.status === 'complete' ? 'bg-teal-50 border-teal-200' : 
                          item.urgent ? 'bg-coral-50 border-coral-300' : 
                          'bg-stone-50 border-stone-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {item.status === 'complete' ? (
                              <CheckCircle className="w-5 h-5 text-teal-600" />
                            ) : item.status === 'in-progress' ? (
                              <div className="w-5 h-5 border border-gold-500 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-gold-500 rounded-full"></div>
                              </div>
                            ) : (
                              <div className="w-5 h-5 border border-stone-400 rounded-full"></div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-1">
                              <h3 className={`font-bold ${
                                item.status === 'complete' ? 'text-teal-700 line-through' : 'text-slate-900'
                              }`}>
                                {item.name}
                              </h3>
                              {item.status !== 'complete' && (
                                <button className="text-xs text-teal-600 font-medium hover:text-teal-700 px-3 py-1 bg-teal-100 rounded">
                                  {item.status === 'in-progress' ? 'Continue' : 'Start'}
                                </button>
                              )}
                            </div>
                            
                            <p className="text-sm text-stone-700 mb-1">{item.details}</p>
                            
                            {item.deadline && item.status !== 'complete' && (
                              <div className={`flex items-center gap-1 text-xs font-medium ${
                                item.urgent ? 'text-coral-700' : 'text-stone-600'
                              }`}>
                                <Clock className="w-3 h-3" />
                                <span>Due: {item.deadline}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Essay Requirements - Expandable */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <button
                    onClick={() => toggleSection('essayPrompts')}
                    className="w-full bg-indigo-50 px-6 py-4 flex items-center justify-between hover:bg-indigo-100 transition-all border-b-2 border-slate-900"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      <div className="text-left">
                        <h2 className="text-md font-bold text-slate-900">Essay Requirements & Prompts</h2>
                        <p className="text-sm text-stone-600">View full essay prompts and requirements</p>
                      </div>
                    </div>
                    {expandedSections.essayPrompts ? (
                      <ChevronDown className="w-5 h-5 text-slate-900" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-900" />
                    )}
                  </button>

                  {expandedSections.essayPrompts && (
                    <div className="p-6 space-y-6 border-t-2 border-slate-900">
                      {/* Common App Essay */}
                      <div className="bg-stone-50 rounded-lg p-5 border border-stone-300">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-slate-900 mb-1">Common Application Essay</h3>
                            <p className="text-sm text-stone-600">650 words maximum</p>
                          </div>
                          <span className="px-2 py-1 bg-gold-500 text-white text-xs font-bold rounded">IN PROGRESS</span>
                        </div>
                        <p className="text-sm text-stone-700 mb-3">
                          Select one of the Common App essay prompts. This essay is shared across all Common App schools.
                        </p>
                        <button className="text-sm text-teal-600 font-medium hover:text-teal-700 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          View Common App Prompts
                        </button>
                      </div>

                      {/* UChicago Supplementals */}
                      <div className="space-y-4">
                        <h3 className="font-bold text-slate-900">UChicago Supplemental Essays</h3>
                        
                        {/* Why UChicago */}
                        <div className="bg-indigo-50 rounded-lg p-5 border border-indigo-300">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-slate-900 mb-1">Essay 1: Why UChicago?</h4>
                              <p className="text-sm text-stone-600">1-2 paragraphs</p>
                            </div>
                            <span className="px-2 py-1 bg-coral-500 text-white text-xs font-bold rounded">NOT STARTED</span>
                          </div>
                          <div className="bg-white rounded p-3 border border-indigo-200 mb-3">
                            <p className="text-sm text-slate-900 italic">
                              "How does the University of Chicago, as you know it now, satisfy your desire for a particular kind of learning, community, and future? Please address with some specificity your own wishes and how they relate to UChicago."
                            </p>
                          </div>
                          <button className="w-full py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-all text-sm flex items-center justify-center gap-2">
                            <Edit className="w-4 h-4" />
                            Start Essay
                          </button>
                        </div>

                        {/* Extended Essay */}
                        <div className="bg-indigo-50 rounded-lg p-5 border border-indigo-300">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-slate-900 mb-1">Essay 2: Extended Essay (Choose 1 prompt)</h4>
                              <p className="text-sm text-stone-600">2-3 pages</p>
                            </div>
                            <span className="px-2 py-1 bg-coral-500 text-white text-xs font-bold rounded">NOT STARTED</span>
                          </div>
                          <div className="space-y-2 mb-3">
                            <div className="bg-white rounded p-3 border border-indigo-200">
                              <p className="text-sm text-slate-900 font-medium mb-1">Option 1: "What's so odd about odd numbers?"</p>
                              <p className="text-xs text-stone-600">Explore mathematical, philosophical, or creative interpretations</p>
                            </div>
                            <div className="bg-white rounded p-3 border border-indigo-200">
                              <p className="text-sm text-slate-900 font-medium mb-1">Option 2: "Pluto was recently reclassified as a dwarf planet. What else should be reclassified and why?"</p>
                            </div>
                            <div className="bg-white rounded p-3 border border-indigo-200">
                              <p className="text-sm text-slate-900 font-medium mb-1">Option 3: "Create your own UChicago-inspired prompt"</p>
                            </div>
                          </div>
                          <button className="text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            View All Essay Options
                          </button>
                        </div>

                        {/* Optional Essay */}
                        <div className="bg-stone-50 rounded-lg p-5 border border-stone-300">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-slate-900 mb-1">Essay 3: Additional Information (Optional)</h4>
                              <p className="text-sm text-stone-600">No word limit</p>
                            </div>
                            <span className="px-2 py-1 bg-stone-400 text-white text-xs font-bold rounded">OPTIONAL</span>
                          </div>
                          <p className="text-sm text-stone-700">
                            Use this space to share anything you'd like the admissions committee to know about you that hasn't been covered elsewhere in your application.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Test Score Policy */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-azure-50 border-b-2 border-slate-900 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 text-azure-600" />
                      <h2 className="text-md font-bold text-slate-900">Standardized Testing Policy</h2>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="bg-azure-50 border-l-4 border-azure-600 rounded-r-lg p-4 mb-4">
                      <h3 className="font-bold text-azure-900 mb-2">Test Optional</h3>
                      <p className="text-sm text-azure-800 leading-relaxed">
                        UChicago has a test-optional policy. You may choose to submit SAT or ACT scores, but they are not required for admission consideration. If you believe your test scores strengthen your application, you should submit them.
                      </p>
                    </div>

                    <div className="bg-stone-50 rounded-lg p-4">
                      <h4 className="font-bold text-slate-900 mb-2 text-sm">Your Test Scores</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-stone-700">SAT Score:</span>
                          <span className="font-bold text-slate-900">1510</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-700">UChicago Middle 50%:</span>
                          <span className="font-bold text-slate-900">1510-1570</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-700">Your Percentile:</span>
                          <span className="font-bold text-teal-700">50th percentile (at bottom of range)</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-indigo-50 rounded border border-indigo-200">
                        <p className="text-xs text-indigo-900">
                          <Lightbulb className="w-3 h-3 inline mr-1" />
                          <strong>Strategic Recommendation:</strong> Your score is at the 50th percentile for admitted students. Consider focusing on strengthening other parts of your application (essays, recommendations) rather than retesting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Quick Info & Resources */}
              <div className="space-y-6">
                
                {/* Deadline Summary */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-coral-50 border-b-2 border-slate-900 px-4 py-3">
                    <h3 className="font-bold text-slate-900 text-sm">Application Deadlines</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="p-3 bg-coral-50 rounded-lg border border-coral-400">
                      <div className="text-coral-700 font-bold text-sm mb-1">Early Decision</div>
                      <div className="text-slate-900 font-bold">November 1, 2024</div>
                      <div className="text-xs text-coral-700 mt-1">29 days remaining</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-300">
                      <div className="text-stone-700 font-bold text-sm mb-1">Early Action</div>
                      <div className="text-slate-900 font-bold">November 1, 2024</div>
                      <div className="text-xs text-stone-600 mt-1">29 days remaining</div>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg border border-stone-300">
                      <div className="text-stone-700 font-bold text-sm mb-1">Regular Decision</div>
                      <div className="text-slate-900 font-bold">January 2, 2025</div>
                      <div className="text-xs text-stone-600 mt-1">92 days remaining</div>
                    </div>
                  </div>
                </div>

                {/* Application Type Info */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h3 className="font-bold text-slate-900 text-sm">Decision Types</h3>
                  </div>
                  <div className="p-4 space-y-3 text-sm">
                    <div>
                      <div className="font-bold text-slate-900 mb-1">Early Decision (ED)</div>
                      <p className="text-xs text-stone-700">Binding commitment. ~18% acceptance rate. Decision by mid-December.</p>
                    </div>
                    <div className="pt-3 border-t border-stone-200">
                      <div className="font-bold text-slate-900 mb-1">Early Action (EA)</div>
                      <p className="text-xs text-stone-700">Non-binding. Similar deadline to ED. Decision by mid-December.</p>
                    </div>
                    <div className="pt-3 border-t border-stone-200">
                      <div className="font-bold text-slate-900 mb-1">Regular Decision (RD)</div>
                      <p className="text-xs text-stone-700">Non-binding. Standard deadline. Decision by late March.</p>
                    </div>
                  </div>
                </div>

                {/* Resources */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h3 className="font-bold text-slate-900 text-sm">Application Resources</h3>
                  </div>
                  <div className="p-3 space-y-1">
                    {[
                      'UChicago Admissions Portal',
                      'Common Application',
                      'Essay Prompts & Guidelines',
                      'First-Year Application Checklist',
                      'Application Fee Waiver Info',
                      'Contact Admissions Office'
                    ].map((link, idx) => (
                      <a
                        key={idx}
                        href="#"
                        className="flex items-center justify-between p-2 rounded hover:bg-stone-50 transition-all group"
                      >
                        <span className="text-sm text-stone-700 group-hover:text-teal-700">{link}</span>
                        <ExternalLink className="w-3 h-3 text-stone-400 group-hover:text-teal-600" />
                      </a>
                    ))}
                  </div>
                </div>

               
              </div>
            </div>
          </div>
        )}

        {/* SCHOOL RESEARCH TAB */}
        {activeTab === 'research' && (
          <div className="space-y-8">
            
            {/* Admission Stats Header */}
            <div className="bg-slate-900 text-white rounded-lg border border-slate-900 overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-slate-700">
                <h2 className="text-md font-bold">Admission Statistics</h2>
                <p className="text-sm text-stone-300 mt-1">Key admission data and acceptance rates</p>
              </div>
              <div className="grid grid-cols-6 gap-4 p-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-coral-400 mb-1">6.4%</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Acceptance Rate</div>
                  <div className="text-xs text-coral-300 mt-1">Highly Selective</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-teal-400 mb-1">~18%</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Early Decision</div>
                  <div className="text-xs text-teal-300 mt-1">Strategic Advantage</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">1510-1570</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Middle 50% SAT</div>
                  <div className="text-xs text-gold-300 mt-1">You: 1540 ✓</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">34-35</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Middle 50% ACT</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">951</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide"># Admitted (2024)</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white mb-1">3.94-4.0</div>
                  <div className="text-xs text-stone-400 uppercase tracking-wide">Middle 50% GPA</div>
                  <div className="text-xs text-gold-300 mt-1">You: 3.87</div>
                </div>
              </div>
            </div>

            {/* Value Proposition - Expandable */}
            <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
              <button
                onClick={() => toggleSection('valueProposition')}
                className="w-full bg-gold-50 px-6 py-4 flex items-center justify-between hover:bg-gold-100 transition-all border-b-2 border-slate-900"
              >
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-gold-600" />
                  <div className="text-left">
                    <h2 className="text-md font-bold text-slate-900">Your Value Proposition</h2>
                    <p className="text-sm text-stone-600">Build your narrative for essays and applications</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-gold-500 text-white text-xs font-bold rounded">AI POWERED</span>
                  {expandedSections.valueProposition ? (
                    <ChevronDown className="w-5 h-5 text-slate-900" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-900" />
                  )}
                </div>
              </button>

              {expandedSections.valueProposition && (
                <div className="grid grid-cols-5 border-t-2 border-slate-900">
                  {/* Left: Student Input */}
                  <div className="col-span-2 p-6 bg-stone-50 border-r-2 border-stone-200">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          What genuinely excites you about this school?
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-teal-600 focus:outline-none"
                          placeholder="Click to add what genuinely excites you..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          How would this school help you grow?
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-teal-600 focus:outline-none"
                          placeholder="Think about how this school advances your goals..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          What makes you different?
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px] focus:border-teal-600 focus:outline-none"
                          placeholder="What makes you unique..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: AI Suggestions */}
                  <div className="col-span-3 p-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">Smart Suggestions</h3>
                      <button className="flex items-center gap-2 px-3 py-1.5 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-all">
                        <RefreshCw className="w-3 h-3" />
                        Refresh
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-gold-50 border border-gold-300 rounded-lg p-4">
                        <p className="text-sm text-slate-900 leading-relaxed mb-3">
                          <strong>Based on your "principled contrarian" approach:</strong> University of Chicago's Core Curriculum would perfectly align with your analytical approach to complex problems, as you'd engage with primary sources from Aristotle to Marx in the Social Sciences sequence while developing the same systematic framework for identifying underlying tensions that you already apply to Supreme Court decisions.
                        </p>
                        <button className="text-xs text-gold-700 font-medium hover:text-gold-800 flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          Click to use this idea
                        </button>
                      </div>

                      <div className="bg-gold-50 border border-gold-300 rounded-lg p-4">
                        <p className="text-sm text-slate-900 leading-relaxed mb-3">
                          <strong>For practical application:</strong> You could leverage UChicago's Metcalf Internship Program to gain the practical policy implementation experience that would complement your theoretical strengths, potentially working with organizations like the Chicago Policy Review or local government offices where your ability to navigate moral complexities in governance would create real impact.
                        </p>
                        <button className="text-xs text-gold-700 font-medium hover:text-gold-800 flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          Click to use this idea
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Strategic Research - Expandable */}
            <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
              <button
                onClick={() => toggleSection('strategicResearch')}
                className="w-full bg-teal-50 px-6 py-4 flex items-center justify-between hover:bg-teal-100 transition-all border-b-2 border-slate-900"
              >
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-teal-600" />
                  <div className="text-left">
                    <h2 className="text-md font-bold text-slate-900">Strategic Research</h2>
                    <p className="text-sm text-stone-600">Deep dive into programs, professors, and opportunities</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-teal-600 text-white text-xs font-bold rounded">AI POWERED</span>
                  {expandedSections.strategicResearch ? (
                    <ChevronDown className="w-5 h-5 text-slate-900" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-900" />
                  )}
                </div>
              </button>

              {expandedSections.strategicResearch && (
                <div className="grid grid-cols-5 border-t-2 border-slate-900">
                  <div className="col-span-2 p-6 bg-stone-50 border-r-2 border-stone-200">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Specific programs & professors
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px]"
                          placeholder="Programs, faculty, and research opportunities..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Campus life & organizations
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px]"
                          placeholder="How you'll engage with campus..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-stone-700 mb-2 uppercase tracking-wide">
                          Location & culture notes
                        </label>
                        <textarea 
                          className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-vertical min-h-[100px]"
                          placeholder="Location, culture, campus vibe..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-span-3 p-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">Smart Suggestions</h3>
                      <button className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-all">
                        <RefreshCw className="w-3 h-3" />
                        Refresh
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-teal-50 border border-teal-300 rounded-lg p-4">
                        <p className="text-sm text-slate-900 leading-relaxed mb-3">
                          <strong>Professor connections:</strong> Professor John Mearsheimer's work on international relations theory and realism would align with your systematic approach to analyzing political tensions. His seminars often challenge students to defend positions under rigorous intellectual scrutiny - perfect for someone with your contrarian analytical style.
                        </p>
                        <button className="text-xs text-teal-700 font-medium hover:text-teal-800 flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          Click to use this idea
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* School Details */}
            <div className="grid grid-cols-3 gap-6">
              {/* Left Column - Main Content */}
              <div className="col-span-2 space-y-6">
                {/* Academic Programs */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-stone-700" />
                      <h3 className="font-bold text-slate-900">Academic Programs</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <p className="text-sm text-stone-700 mb-2"><strong>Class Sizes:</strong> 72% classes under 20 students, 6% classes over 50 students</p>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm font-bold text-slate-900 mb-2">Popular Majors:</p>
                      <div className="flex flex-wrap gap-2">
                        {['Economics', 'Mathematics', 'Computer Science', 'Political Science', 'Biology', 'Psychology', 'English', 'Physics', 'History', 'Philosophy'].map(major => (
                          <span key={major} className="px-3 py-1 bg-teal-100 text-teal-800 text-xs font-medium rounded-full">
                            {major}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-stone-700"><strong>Special Programs:</strong> Core Curriculum, UROP, Metcalf Internship Program, Study abroad in 40+ countries, Combined BA/MA programs</p>
                    </div>
                  </div>
                </div>

                {/* Campus Culture */}
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-stone-700" />
                      <h3 className="font-bold text-slate-900">Campus Culture & Values</h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="bg-gold-50 border-l-4 border-gold-500 rounded-r-lg p-4">
                      <h4 className="font-bold text-gold-900 text-sm mb-1">Intense Academic Environment</h4>
                      <p className="text-sm text-gold-800">Known for rigorous coursework and intellectual discourse. Students thrive on challenging conventional wisdom.</p>
                    </div>
                    <div className="bg-azure-50 border-l-4 border-azure-500 rounded-r-lg p-4">
                      <h4 className="font-bold text-azure-900 text-sm mb-1">Quirky Traditions</h4>
                      <p className="text-sm text-azure-800">Scav Hunt, Kuviasungnerk/Kangeiko traditions that build unique community bonds.</p>
                    </div>
                    <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4">
                      <h4 className="font-bold text-indigo-900 text-sm mb-1">Strong Debate Culture</h4>
                      <p className="text-sm text-indigo-800">Campus culture encourages vigorous intellectual discussion and challenging ideas.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Stats */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h4 className="font-bold text-slate-900 text-sm">Campus Life</h4>
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Total Students</span>
                      <span className="font-bold text-slate-900">7,470</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Undergrads</span>
                      <span className="font-bold text-slate-900">~6,800</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Setting</span>
                      <span className="font-bold text-slate-900">Urban</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Location</span>
                      <span className="font-bold text-slate-900">Chicago, IL</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h4 className="font-bold text-slate-900 text-sm">Financial Overview</h4>
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Tuition & Fees</span>
                      <span className="font-bold text-slate-900">$64,965</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Room & Board</span>
                      <span className="font-bold text-slate-900">$19,485</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-stone-200">
                      <span className="text-stone-600">Total Cost</span>
                      <span className="font-bold text-slate-900">$84,450</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Avg. Aid</span>
                      <span className="font-bold text-teal-700">$56,050</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-slate-900 overflow-hidden">
                  <div className="bg-stone-100 border-b-2 border-slate-900 px-4 py-3">
                    <h4 className="font-bold text-slate-900 text-sm">Outcome Metrics</h4>
                  </div>
                  <div className="p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-600">4-Year Grad</span>
                      <span className="font-bold text-slate-900">93%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">6-Year Grad</span>
                      <span className="font-bold text-slate-900">95%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Alumni Salary</span>
                      <span className="font-bold text-slate-900">$75k+ avg</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Complete Breakdown - Expandable */}
            <button
              onClick={() => toggleSection('completeBreakdown')}
              className="w-full bg-stone-200 rounded-lg px-6 py-4 flex items-center justify-between hover:bg-stone-300 transition-all border border-stone-400"
            >
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-stone-700" />
                <span className="text-md font-bold text-slate-900">View Complete Statistical Breakdown & Detailed Research</span>
              </div>
              {expandedSections.completeBreakdown ? (
                <ChevronDown className="w-5 h-5 text-slate-900" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-900" />
              )}
            </button>

            {expandedSections.completeBreakdown && (
              <div className="bg-stone-50 rounded-lg border border-stone-300 p-8 text-center">
                <p className="text-stone-600 italic">
                  Here would be all the comprehensive detailed statistics, admission trends, demographics, geographic distribution, financial aid details, and any other research data...
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeStrategyPage;